import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Alert,
  Divider
} from '@mui/material';
import { Cookie as CookieIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { trackUserAction } from '../utils/analytics';

const ConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
      // Initialize analytics based on saved preferences
      if (savedPreferences.analytics) {
        initializeAnalytics();
      }
    }
  }, []);

  const initializeAnalytics = () => {
    // Enable Google Analytics if consent given
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const disableAnalytics = () => {
    // Disable Google Analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    setPreferences(newPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    localStorage.setItem('consent-timestamp', new Date().toISOString());
    
    initializeAnalytics();
    trackUserAction('consent_accept_all');
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    setPreferences(newPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    localStorage.setItem('consent-timestamp', new Date().toISOString());
    
    disableAnalytics();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('consent-timestamp', new Date().toISOString());
    
    if (preferences.analytics) {
      initializeAnalytics();
      trackUserAction('consent_analytics_accepted');
    } else {
      disableAnalytics();
    }
    
    trackUserAction('consent_custom_saved');
    setShowBanner(false);
    setShowDetails(false);
  };

  const handlePreferenceChange = (category) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Main Banner */}
      <Paper
        elevation={3}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1300,
          p: 3,
          maxWidth: 600,
          mx: 'auto'
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CookieIcon color="primary" />
            <Typography variant="h6" component="h2">
              Cookie Preferences
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            We use cookies to enhance your experience, analyze site traffic, and personalize content. 
            By continuing to use our site, you consent to our use of cookies.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip 
              label="Necessary" 
              color="primary" 
              variant="outlined"
              size="small"
            />
            {preferences.analytics && (
              <Chip 
                label="Analytics" 
                color="secondary" 
                variant="outlined"
                size="small"
              />
            )}
            {preferences.marketing && (
              <Chip 
                label="Marketing" 
                color="secondary" 
                variant="outlined"
                size="small"
              />
            )}
            {preferences.functional && (
              <Chip 
                label="Functional" 
                color="secondary" 
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => setShowDetails(true)}
              startIcon={<SettingsIcon />}
            >
              Customize
            </Button>
            <Button
              variant="outlined"
              onClick={handleRejectAll}
              color="error"
            >
              Reject All
            </Button>
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              color="primary"
            >
              Accept All
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Preferences Dialog */}
      <Dialog 
        open={showDetails} 
        onClose={() => setShowDetails(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon color="primary" />
            Cookie Preferences
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              You can customize which types of cookies you allow. Necessary cookies are always required for the site to function.
            </Alert>

            <Box>
              <Typography variant="h6" gutterBottom>
                Cookie Types
              </Typography>
              
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.necessary}
                      disabled
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Necessary Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Required for basic site functionality. Cannot be disabled.
                      </Typography>
                    </Box>
                  }
                />

                <Divider />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Analytics Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Help us understand how visitors interact with our website.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Marketing Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Used to deliver personalized advertisements and track campaign performance.
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.functional}
                      onChange={() => handlePreferenceChange('functional')}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Functional Cookies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable enhanced functionality and personalization.
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} variant="contained">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsentBanner;